import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categoryData, getSubcategoryImages } from '../data/categoryData';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
// iter114d: useAuth + UserDropdown imports removed — auth widget
// now lives exclusively in TopNav.
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Seo } from '../components/Seo';
import { SITE, productJsonLd } from '../data/seoData';

const SubcategoryDetail = () => {
  const { categoryId, productId } = useParams();
  // iter114d: removed `const { isAuthenticated } = useAuth();` — no
  // longer needed; auth widget is in TopNav.

  const category = categoryData[categoryId];
  const subcategory = category?.subcategories?.find(sub => sub.id === productId);

  // Memoized so the Breadcrumbs component doesn't see a new items array
  // on each render (fixes a RR-flagged inline-prop issue). Declared BEFORE
  // the early return so it satisfies react-hooks/rules-of-hooks.
  const breadcrumbItems = useMemo(
    () => [
      { label: 'Products', to: '/products' },
      { label: category?.name || '', to: `/category/${categoryId}` },
      { label: subcategory?.name || '' },
    ],
    [category?.name, categoryId, subcategory?.name]
  );

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Category Not Found</h1>
          <Link to="/splash">
            <Button className="bg-[#6FBF73] hover:bg-[#4A7C59]">Back to Categories</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Helper function to determine quote parameters based on subcategory
  const getQuoteParams = () => {
    // Map the subcategory page category IDs to QuoteCalculator product IDs
    const productTypeMap = {
      'stickers': 'stickers',
      'labels': 'labels',
      'decals-clings': 'decals',
      'wearables': 'iron-ons',
      'unique-custom-products': 'magnets',
      'unique-custom': 'magnets',
      'specialty-niche-products': 'specialty',
      'specialty-niche': 'specialty'
    };

    // Use the actual subcategory/product ID from the URL as the format
    // This ensures the correct product is selected in QuoteCalculator
    const productType = productTypeMap[categoryId] || 'stickers';
    const formatId = productId; // Use the actual product ID from the URL

    return `?product=${productType}&format=${formatId}&step=3`;
  };

  if (!subcategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Product Not Found</h1>
          <Link to={`/category/${categoryId}`}>
            <Button className="bg-[#6FBF73] hover:bg-[#4A7C59]">Back to {category.name}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = getSubcategoryImages(category.folder, subcategory.folder);

  const seoDescription = `Order custom ${subcategory.name.toLowerCase()} online — any size, shape & quantity. Instant quotes, live artwork preview and fast shipping across Canada & the USA.`;
  const seoImage = images && images[0] ? `${SITE.url}${images[0]}` : undefined;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Seo
        title={`${subcategory.name} — Custom Printed Online | Evergreen Graphics Group`}
        description={seoDescription}
        path={`/category/${categoryId}/product/${productId}`}
        image={seoImage}
        jsonLd={productJsonLd({
          name: subcategory.name,
          description: seoDescription,
          path: `/category/${categoryId}/product/${productId}`,
          image: seoImage,
        })}
      />
      {/* iter114s.44 — Page-level brand band removed. The TopNav
          (logo + brand + nav) and the global breadcrumb sub-strip
          already cover navigation context, so a third stacked
          header on this page just duplicated the chrome. The
          subcategory name + parent-category + back-arrow that used
          to live up here are still visible inside the hero and the
          page content below. */}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-6">
          <Breadcrumbs
            items={breadcrumbItems}
          />
        </div>

        {/* Hero Image */}
        <div className="mb-12">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={images.main}
              alt={subcategory.name}
              className="w-full h-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        </div>

        {/* Summary Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-green-100">
            <h2 className="text-3xl font-bold text-[#4A7C59] mb-6">Product Summary</h2>
            <div className="space-y-4">
              <img
                src={images.summary}
                alt={`${subcategory.name} Summary`}
                className="w-full rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-green-100">
            <h2 className="text-3xl font-bold text-[#4A7C59] mb-6">Product Features</h2>
            <div className="space-y-4">
              <img
                src={images.features}
                alt={`${subcategory.name} Features`}
                className="w-full rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
        </div>

        {/* Text Content Section */}
        <div className="bg-gradient-to-br from-[#4A7C59] to-[#6FBF73] rounded-2xl shadow-xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Product Details</h2>
          <div className="bg-white/95 rounded-lg p-6">
            <img
              src={images.text}
              alt={`${subcategory.name} Details`}
              className="w-full"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-[#4A7C59] to-[#6FBF73] rounded-2xl shadow-xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Order?</h2>
          <p className="text-xl mb-8 text-green-100">Get an instant quote for {subcategory.name}</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to={`/quote${getQuoteParams()}`}>
              <Button size="lg" className="bg-white text-[#4A7C59] hover:bg-green-50 text-lg px-8 py-6">
                Get Instant Quote <ArrowRight className="ml-2" />
              </Button>
            </Link>
            <Link to={`/category/${categoryId}`}>
              <Button size="lg" variant="outline" className="bg-white/10 text-white border-white hover:bg-white/20 text-lg px-8 py-6">
                View More {category.name}
              </Button>
            </Link>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between items-center">
          <Link to={`/category/${categoryId}`}>
            <Button variant="outline" className="border-[#4A7C59] text-[#4A7C59] hover:bg-green-50">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back to {category.name}
            </Button>
          </Link>
          <Link to="/splash">
            <Button variant="outline" className="border-[#4A7C59] text-[#4A7C59] hover:bg-green-50">
              All Categories
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#4A7C59] text-white pt-12 pb-16 mt-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img
                src="/eg3-logo.png"
                alt="Evergreen Graphics Group"
                className="h-24 w-24 mb-4 rounded-full"
                style={{ objectFit: 'contain' }}
              />
              <p className="text-green-100">Professional print solutions for your business needs.</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-3">Contact Us</h4>
              <p className="text-green-100 mb-2">Phone: (905) 447-0257</p>
              <p className="text-green-100 mb-2">Phone: 289 257 1519</p>
              <p className="text-green-100 mb-2">Email: info@eg3.ca</p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-3">Location</h4>
              <p className="text-green-100">
                90 Venture Dr #2<br />
                Scarborough, ON M1B 3L6<br />
                Canada
              </p>
            </div>
          </div>
          <div className="border-t border-green-600 mt-6 pt-6 text-center text-green-100">
            <p>© 2026 Evergreen Graphics Group Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SubcategoryDetail;
